import styled from 'styled-components';
import CategoryItem from './CategoryItem';

interface CategorySidebarProps {
  categories: string[];
  selectedCategory: string | null;
  selectedOptionKey: string | null;
  onCategorySelect: (category: string) => void;
  onOptionKeySelect: (category: string, optionKey: string) => void;
  layerUsesProductOptions: (category: string) => boolean;
  getOptionKeysForLayer: (category: string) => string[];
  onBackClick: () => void;
}

/**
 * Category sidebar component - displays list of product layers (categories)
 * Layers with product_options show indented option keys beneath the category.
 */
const CategorySidebar: React.FC<CategorySidebarProps> = ({
  categories,
  selectedCategory,
  selectedOptionKey,
  onCategorySelect,
  onOptionKeySelect,
  layerUsesProductOptions,
  getOptionKeysForLayer,
  onBackClick,
}) => {
  const validCategories = categories.filter((cat) => cat && cat.trim().length > 0);

  if (validCategories.length === 0) {
    return (
      <Container>
        <Title>Categories</Title>
        <EmptyMessage>No categories available</EmptyMessage>
      </Container>
    );
  }

  return (
    <Container>
      <BackButton onClick={onBackClick}>
        <BackButtonIcon>
          <img src='/img/back.svg' alt='back pict'/>
        </BackButtonIcon>
        <BackButtonText>
          GALERIE INSPIRATION
        </BackButtonText>
      </BackButton>
      <Title>Choisissez vos options</Title>
      <CategoryList>
        {validCategories.map((category) => {
          const hasOptions = layerUsesProductOptions(category);
          const optionKeys = hasOptions ? getOptionKeysForLayer(category) : [];
          const isCategorySelected = selectedCategory === category;

          return (
            <CategoryGroup key={category}>
              <CategoryItem
                category={category}
                selected={isCategorySelected && !selectedOptionKey}
                onClick={() => onCategorySelect(category)}
              />
              {hasOptions && isCategorySelected && optionKeys.length > 0 && (
                <OptionKeyList>
                  {optionKeys.map((optionKey) => (
                    <OptionKeyItem
                      key={optionKey}
                      selected={selectedOptionKey === optionKey}
                      onClick={() => onOptionKeySelect(category, optionKey)}
                    >
                      {optionKey}
                    </OptionKeyItem>
                  ))}
                </OptionKeyList>
              )}
            </CategoryGroup>
          );
        })}
      </CategoryList>
    </Container>
  );
};

const Container = styled.div`
  padding: 1rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 4px;

    &:hover {
      background: rgba(255, 255, 255, 0.5);
    }
  }
`;

const BackButton = styled.div`
  position: relative;
  top: 0;
  background: ${props => props.theme.colors.backButtonBackground};
  border-radius: 5px;
  padding: .5vh 1vw;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  transition: opacity .2s ease-out;
  &:hover{
    cursor: pointer;
    opacity: .9;
  }
`

const BackButtonIcon = styled.span`
  margin-right: 1vw;
`

const BackButtonText = styled.span`
  font-family: Poppins-SemiBold, sans-serif;
  font-size: 0.9rem;
  color: #fff;
`

const Title = styled.h2`
  color: #ffffff;
  font-family: Poppins-ExtraBold, sans-serif;
  font-size: 1.2rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const CategoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
`;

const CategoryGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const OptionKeyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-left: 1.25rem;
  padding-left: 0.75rem;
  border-left: 2px solid rgba(255, 255, 255, 0.35);
`;

const OptionKeyItem = styled.button<{ selected: boolean }>`
  display: flex;
  align-items: center;
  padding: 0.45rem 0.75rem;
  border: none;
  border-radius: 3px;
  background-color: ${({ selected }) => (selected ? '#C37427' : 'rgba(255, 255, 255, 0.15)')};
  color: #ffffff;
  font-family: Poppins-SemiBold, sans-serif;
  font-size: 0.95rem;
  text-transform: capitalize;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ selected }) => (selected ? '#C37427' : 'rgba(255, 255, 255, 0.25)')};
  }
`;

const EmptyMessage = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
  text-align: center;
  padding: 2rem 1rem;
  font-style: italic;
`;

export default CategorySidebar;
